<script setup>
import { computed } from 'vue'
import { cdnImageUrl } from '@dpgradio/creative'

import HookPlayer from './HookPlayer.ce.vue'
import TrackMovement from './TrackMovement.ce.vue'
import TrackExternalLinks from './TrackExternalLinks.ce.vue'

const props = defineProps({
  position: {
    type: Number,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  artist: {
    type: Object,
    required: true
  },
  stats: {
    type: Object,
    required: false
  },
  thumbnail: {
    type: String,
    required: false
  },
  hooks: {
    type: Object,
    required: false
  },
  selectorCode: {
    type: String,
    required: false
  },
  spotifyUrl: {
    type: String,
    required: false
  },
  appleMusicUrl: {
    type: String,
    required: false
  },
  youtubeUrl: {
    type: String,
    required: false
  },
})

const coverUrl = computed(() => cdnImageUrl(props.thumbnail, 'w480'))
</script>

<template>
  <div class="chart-track">
    <div class="track-position">
      {{ position }}
    </div>

    <div class="track-cover">
      <img v-if="thumbnail" :src="coverUrl" :alt="title" />
    </div>

    <div class="track-content">
      <div class="track-meta" v-if="stats">
        <TrackMovement :stats="stats" />
      </div>

      <div class="track-title">
        {{ title }}
      </div>
      <div class="track-artist">
        {{ artist?.name }}
      </div>
    </div>
    <div class="track-actions">
      <HookPlayer :url="hooks.m4a" v-if="hooks?.m4a" :track-title="title" :artist="artist?.name" />
      <TrackExternalLinks
        :spotify-url="spotifyUrl"
        :apple-music-url="appleMusicUrl"
        :youtube-url="youtubeUrl"
      />
    </div>
  </div>
</template>

<style lang="scss">
.chart-track {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  font-family: var(--font-title);
}

.track-position {
  min-width: 2.5rem;
  text-align: right;
  font-size: 1.5rem;
  font-weight: 500;
  color: #9595a0;
}

.track-cover {
  flex: 0 0 auto;
  width: 70px;
  height: 70px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.track-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.track-meta {
  flex: 0 0 auto;
  font-size: 0.8rem;
}

.track-title {
  font-size: 1.25rem;
  font-family: var(--font-title);
  text-transform: var(--font-title-transform, none);
  font-weight: var(--font-title-bold, 600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgb(var(--color-primary-text));
}

.track-artist {
  font-size: 1.1rem;
  text-transform: uppercase;
  color: rgb(var(--color-secondary-text));
}

.track-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>